<template>
  <v-dialog width="555" v-bind="{ ...$props, ...$attrs }" scrim @update:model-value="$emit('update:model-value', $event)">
    <v-card max-width="555" variant="flat">
      <v-card-title>Grant Full Access to Demographic Survey Report</v-card-title>
      <v-card-text>
        <GrantAccessUserSearch
          :async-params="{ with_dsr_disabled_only: true }"
          :max-users="20"
          @select-user="usersToGrantAccess = $event"
        />
        <p>
          This will grant the user access to candidate gender and race distribution insights on the
          <a
            href="/app/insights/demographic-survey-report?tab=survey"
            target="_blank"
            rel="noopener noreferrer"
          >
            Survey Insights tab
          </a>
          in the Demographic Survey Report.
        </p>
        <br>
        <p>An e-mail will be sent to this user notifying them of this change.</p>
      </v-card-text>
      <v-card-actions>
        <DpplNewButton
          :disabled="!usersToGrantAccess?.userIds?.length"
          color="primary"
          variant="flat"
          :loading="grantingAccess"
          @click="handleGrantAccess"
        >
          Grant Full Report Access
        </DpplNewButton>
        <DpplNewButton
          color="textDarkest"
          variant="outlined"
          @click="$emit('update:model-value', false)"
        >
          Cancel
        </DpplNewButton>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import DpplNewButton from '@/components/datapeople/DpplNewButton.vue';
import { ref, computed } from 'vue';
import GrantAccessUserSearch from '@/components/settings/team/GrantAccessUserSearch.vue';
import { grantAccessToDemographicSurveyReport } from '@/api/settings';
import { useAppSnackbar } from '@/stores/app/snackbar';

const emit = defineEmits(['update:model-value', 'granted-access']);

const usersToGrantAccess = ref([]);
const grantingAccess = ref(false);

const { addSnackbar, addErrorSnackbar } = useAppSnackbar();

const snackbarText = computed(() => {
  const userEmails = usersToGrantAccess.value.userEmails || [];

  return (userEmails.length === 1
    ? `${userEmails[0]} has been granted full report access`
    : `${userEmails[0]} +${userEmails.length - 1} more user${userEmails.length > 2 ? 's have' : ' has'} been granted full report access`);
});

const handleGrantAccess = async () => {
  try {
    grantingAccess.value = true;

    await grantAccessToDemographicSurveyReport(usersToGrantAccess.value.userIds);

    emit('granted-access');

    addSnackbar({
      text: snackbarText.value,
      color: 'success',
    });

    setTimeout(() => {
      usersToGrantAccess.value = [];
    }, 500);
  } catch (error) {
    addErrorSnackbar({
      text: 'There was an error granting access to the selected users.',
    });
    throw error;
  } finally {
    grantingAccess.value = false;
    emit('update:model-value', false);
  }
};
</script>

<style scoped lang="less">
:deep(.v-card) {
  background: @white;
  border-radius: 8px !important;
  padding: 0 !important;
}

:deep(.v-card-title) {
  padding: 16px;
  text-align: left;
  font-size: 15px;
  font-weight: 500;
  line-height: 150%;
  border-bottom: 1px solid @stroke-default;
}

:deep(.v-card-text) {
  padding: 16px;

  p {
    font-size: 13px;
    font-weight: 500;
    line-height: 150%;
    color: @text-darkest;
  }

  a {
    color: @text-brand;
    cursor: pointer;
    font-weight: 700;
  }

  strong {
    font-weight: 700;
  }
}

:deep(.v-card-actions) {
  padding: 16px;
}
</style>
